import { Injectable } from "@angular/core";
import { lastValueFrom } from "rxjs";
import { apiCallWrapper } from "../../api/api.util";
import { FormResponseApi } from "../../api/forms/form-response.api";
import { TFormResponseEdit } from "../../model/forms/form-response.model";
import { IMergeItem, IUdnoMergeItem } from "../../model/forms/merge-result.model";
import { ToastService } from "../toast.service";

@Injectable({
  providedIn: "root",
})
export class FormResponseService {
  constructor(private toastService: ToastService, private formResponseApi: FormResponseApi) {}

  public create = async (data: Omit<TFormResponseEdit, "id">) => {
    try {
      const response = await lastValueFrom(
        apiCallWrapper(this.formResponseApi.create(data), {
          toastService: this.toastService,
          action: "Create response",
          failTitle: "Failed to submit response",
          successTitle: "Response successfully submitted",
        })
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  public async update(data: TFormResponseEdit): Promise<boolean> {
    try {
      const response = await lastValueFrom(
        apiCallWrapper(this.formResponseApi.update(data.id, data), {
          toastService: this.toastService,
          action: "Edit response",
          failTitle: "Failed to edit response",
          successTitle: "Successfully edited response",
        })
      );
      return !!response;
    } catch (error) {
      throw error;
    }
  }

  public async delete(responseId: number | string): Promise<boolean> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.formResponseApi.delete(responseId), {
          toastService: this.toastService,
          action: "Delete response",
          failTitle: "Failed to delete response",
          successTitle: "Successfully deleted response",
        })
      );
      return !!(data && data.success);
    } catch (error) {
      throw error;
    }
  }

  public readScansLocations = (responseId: number | string) => {
    return apiCallWrapper(this.formResponseApi.readScansLocations(responseId), {
      toastService: this.toastService,
      action: "Fetch scans urls",
      failTitle: "Failed to fetch scans urls",
      successTitle: "Successfully fetched scans urls",
    });
  };

  public merge = (data: {
    mergeItems: IMergeItem[];
    isPreview: boolean;
    isOverwriteExistingResponses: boolean;
  }) => {
    return apiCallWrapper(this.formResponseApi.merge(data), {
      toastService: this.toastService,
      action: data.isPreview ? "Preview merge responses" : "Merge responses",
      failTitle: `Failed to ${data.isPreview ? "preview " : ""}merge responses`,
      successTitle: `Successfully ${data.isPreview ? "preview " : ""}merged responses`,
    });
  };

  public undomerge = (data: { undomergeItems: IUdnoMergeItem[] }) => {
    return apiCallWrapper(this.formResponseApi.undomerge(data), {
      toastService: this.toastService,
      action: "Undo merge responses",
      failTitle: `Failed to undo merge responses`,
      successTitle: `Successfully undo merged responses`,
    });
  };
}
